import { Button } from '@/components/shadcn/button'
import { MessagesCollaborationThreadDetails } from './Collaboration'
import { IconArrowLeftOutline } from '@icons/IconArrowLeftOutline'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { FormComponentLoading } from '../../../FormComponent'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { Suspense, useCallback } from 'react'
import { AvatarFallback } from '@/components/shadcn/avatar'
import { Avatar } from '@/components/shadcn/avatar'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { getInitials } from '@/lib/utils'
import { format } from 'date-fns'
import { CreateLogEntryForm } from './CreateLogEntry'
import { Separator } from '@/components/shadcn/separator'
import { ReopenOrDeleteActions } from './ReopenOrDeleteActions'
import Timeline from '@/catalog-apps/components/SupportPanel/TemplatesV2/ActivityLogV2/Timeline'
import {
  TimeLineItem,
  TimelineItemContent,
  TimelineItemIcon,
} from '@/catalog-apps/components/SupportPanel/DataAssetsV2/ActivityLogV2/TimeLineItem'
import { IconCommentOutline } from '@icons/IconCommentOutline'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconAccountSwitchOutline } from '@icons/IconAccountSwitchOutline'
import { IconBg } from '@/components/ui/IconBg'
import { IconReturnOutline } from '@icons/IconReturnOutline'

type MessagesCollaborationCollaborationLog = RestSchemas['Messages.Collaboration.CollaborationLog']

type ThreadContentProps = {
  selectedThread: MessagesCollaborationThreadDetails
  setSelectedThreadId: React.Dispatch<React.SetStateAction<number | undefined>>
  invalidateThread: () => void
}

const IconMap = {
  comment: IconCommentOutline,
  resolved: IconCheckMarkOutline,
  reassigned: IconAccountSwitchOutline,
  reopened: IconReturnOutline,
}

export function ThreadContent({ selectedThread, setSelectedThreadId, invalidateThread }: ThreadContentProps) {
  const { assignedToUser, description, createdByUser, date } = selectedThread
  return (
    <>
      <div className="bg-offwhite/80 sticky top-0 z-20 border-b pb-2 backdrop-blur">
        <div className="flex flex-col gap-y-2">
          <Button onClick={() => setSelectedThreadId(undefined)} variant="link" className="justify-start p-0">
            <IconArrowLeftOutline />
            <span className="ml-1">Back to threads</span>
          </Button>
          <div className="grid grid-cols-[auto_1fr] items-center gap-8 rounded-xl bg-background px-5 py-3 shadow">
            <TooltipButton tooltip={assignedToUser || '?'}>
              <Avatar className="size-16">
                <AvatarFallback className="bg-accent text-accent-foreground">
                  {assignedToUser ? getInitials(assignedToUser) : '?'}
                </AvatarFallback>
              </Avatar>
            </TooltipButton>
            <p className="text-muted-foreground">{description}</p>
          </div>
          <div className="pr-2 text-right text-xs">
            <span className="font-light italic text-muted-foreground">Created by:</span> {createdByUser}{' '}
            {date ? ` - ${format(new Date(date), 'PPp')}` : ''}
          </div>
        </div>
      </div>
      <QueryErrorBoundary errorMessage="Failed to fetch thread content">
        <Suspense fallback={<ThreadContentLoading />}>
          <Content selectedThread={selectedThread} invalidateThread={invalidateThread} />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}

function Content({ selectedThread, invalidateThread }: Omit<ThreadContentProps, 'setSelectedThreadId'>) {
  const { getCollaborationApiGetThreadWithLogs } = useRestClient()

  const { data: threadWithLogs } = getCollaborationApiGetThreadWithLogs.useSuspenseQuery({
    path: { threadId: selectedThread.id! },
  })

  const invalidateThreadAndLogs = useCallback(() => {
    getCollaborationApiGetThreadWithLogs.invalidateQueries({
      parameters: {
        path: { threadId: selectedThread.id! },
      },
    })
    invalidateThread()
  }, [getCollaborationApiGetThreadWithLogs, invalidateThread, selectedThread.id])

  const { collaborationChannelId: channelId } = selectedThread

  const { logs = [] } = threadWithLogs
  const lastLogEntry = logs?.length ? logs[logs.length - 1] : null

  const isResolved = lastLogEntry?.state === 'Resolved'

  return (
    <>
      <div className="w-full">
        <Timeline>
          {logs?.map((log) => {
            const isResolved = log.state === 'Resolved'
            const isReassigned = !!log.assigneeUser
            const isReopened = log.state === 'Open' && !log.post
            return (
              <TimeLineItem key={log.id}>
                <TimelineItemIcon
                  Icon={
                    isReassigned
                      ? IconMap.reassigned
                      : isResolved
                        ? IconMap.resolved
                        : isReopened
                          ? IconMap.reopened
                          : IconMap.comment
                  }
                  className={'bg-offwhite'}
                />
                <TimelineItemContent>
                  <LogItem log={log} isResolved={isResolved} isReassigned={isReassigned} isReopened={isReopened} />
                </TimelineItemContent>
              </TimeLineItem>
            )
          })}
        </Timeline>
        {/* <pre className="text-xs">{JSON.stringify(threadWithLogs, null, 2)}</pre> */}
      </div>
      <div className="bg-offwhite/80 sticky bottom-0 space-y-5 backdrop-blur">
        <Separator />
        {isResolved ? (
          <ReopenOrDeleteActions
            channelId={channelId}
            threadId={selectedThread.id}
            invalidateThreadAndLogs={invalidateThreadAndLogs}
          />
        ) : (
          <CreateLogEntryForm
            lastLogEntry={lastLogEntry}
            channelId={channelId}
            threadId={selectedThread.id}
            assignee={threadWithLogs?.thread?.assignedTo}
            invalidateThreadAndLogs={invalidateThreadAndLogs}
          />
        )}
      </div>
    </>
  )
}

function ThreadContentLoading() {
  return <FormComponentLoading />
}

function LogItem({
  log,
  isResolved,
  isReassigned,
  isReopened,
}: {
  log: MessagesCollaborationCollaborationLog
  isResolved: boolean
  isReassigned: boolean
  isReopened: boolean
}) {
  return (
    <div className="flex w-full flex-col gap-y-2">
      {/* <div className="grid grid-cols-[33px_1fr] items-center rounded-full border"> */}
      <div className="flex w-full flex-col">
        <div className="flex items-center justify-between gap-x-2 border-b pb-0.5">
          <div className="flex items-center gap-x-1">
            <Avatar className="size-7">
              <AvatarFallback className="bg-accent text-xs text-accent-foreground">
                {log.createdByUser ? getInitials(log.createdByUser) : '?'}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs">{log.createdByUser}</span>
          </div>
          <span className="flex gap-x-5 text-xs">{log.date && <span>{format(new Date(log.date), 'PPp')}</span>}</span>
        </div>
        {isReassigned && (
          <>
            {/* <Separator /> */}
            <div className="flex items-center gap-x-2 rounded-l-sm rounded-r-sm border-l-4 border-secondary bg-secondary/5 px-3 py-1 text-xs">
              <span className="font-light">Reassigned thread to:</span>
              <Avatar className="size-6">
                <AvatarFallback className="bg-secondary text-2xs text-accent-foreground">
                  {getInitials(log.assigneeUser!)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs">{log.assigneeUser}</span>
            </div>
          </>
        )}
        {isResolved && (
          <div className="flex items-center gap-x-2 rounded-l-sm rounded-r-sm border-l-4 border-green-500 bg-green-500/5 px-3 py-1 text-xs">
            <IconBg className="size-6 border border-green-500 bg-transparent">
              <IconCheckMarkOutline className="text-green-500" />
            </IconBg>
            <span className="font-light">Resolved thread</span>
          </div>
        )}
        {isReopened && (
          <div className="flex items-center gap-x-2 rounded-l-sm rounded-r-sm border-l-4 border-blue-500 bg-blue-500/5 px-3 py-1 text-xs">
            <IconBg className="size-6 border border-blue-500 bg-transparent">
              <IconReturnOutline className="text-blue-500" />
            </IconBg>
            <span className="font-light">Reopened thread</span>
          </div>
        )}
      </div>
      {log.post && (
        <div className="flex">
          <div className="rounded-xl bg-background/50 p-2 px-5">
            <span className="whitespace-pre-wrap text-sm leading-tight text-muted-foreground">{log.post}</span>
          </div>
        </div>
      )}
    </div>
  )
}
